import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Shield, Clock, Calculator, Mail, Info, Crown, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function RefundPolicyPage() {
  const [showCalculator, setShowCalculator] = useState(false);
  const [usedConsultations, setUsedConsultations] = useState(0);

  const calculateRefund = () => {
    const consultationCost = 40 / 6; // €6.67 per consultation (€40 total / 6 consultations)
    const totalConsultationFee = 40;
    const refundAmount = Math.max(0, totalConsultationFee - (usedConsultations * consultationCost));
    return refundAmount.toFixed(2);
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Grįžti
            </Button>
          </Link>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-purple-500/50">
            <Shield className="w-8 h-8 text-purple-400" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-amber-400 mb-2">
            Prenumeratos ir grąžinimo politika
          </h1>
          <p className="text-muted-foreground">
            NUMENAXIS – savęs pažinimo erdvė
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* General Info Card */}
          <Card className="bg-gradient-to-br from-violet-950/50 via-indigo-950/50 to-slate-950/50 border-amber-500/30 p-6">
            <h2 className="text-xl font-semibold text-amber-400 mb-4 flex items-center gap-2">
              <Info className="w-5 h-5" />
              Bendroji informacija
            </h2>
            <div className="text-foreground/80 space-y-3">
              <p>
                NUMENAXIS siūlo kelis prenumeratos planus, pritaikytus skirtingiems poreikiams. 
                Mes vertiname jūsų pasitikėjimą ir norime užtikrinti, kad jaustumėtės saugiai.
              </p>
              <p>
                Mūsų tikslas – kurti prasmingą ryšį, todėl suprantame, kad kartais reikia laiko 
                įvertinti, ar paslauga atitinka jūsų lūkesčius.
              </p>
            </div>
          </Card>

          {/* VIP Plans Overview */}
          <Card className="bg-gradient-to-br from-violet-950/50 via-indigo-950/50 to-slate-950/50 border-purple-500/30 p-6">
            <h2 className="text-xl font-semibold text-purple-400 mb-4 flex items-center gap-2">
              <Crown className="w-5 h-5" />
              VIP planų apžvalga
            </h2>
            
            <div className="space-y-4">
              {/* Monthly Plan */}
              <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/30">
                <h3 className="font-semibold text-amber-400 mb-2">Mėnesinis planas (€2.99/mėn.)</h3>
                <p className="text-sm text-foreground/70">
                  Prenumeratą galima atšaukti bet kuriuo metu. Mokėjimas automatiškai sustoja 
                  nuo kito mėnesio. Grąžinimai netaikomi už jau praėjusį laikotarpį.
                </p>
              </div>

              {/* Yearly Plan */}
              <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
                <h3 className="font-semibold text-green-400 mb-2">Metinis planas (€25/metus)</h3>
                <p className="text-sm text-foreground/70">
                  Prenumeratą galima atšaukti bet kuriuo metu. Grąžinimai svarstomi individualiai 
                  per pirmąsias 14 dienų nuo įsigijimo.
                </p>
              </div>

              {/* Premium Plan with Consultations */}
              <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/30">
                <h3 className="font-semibold text-purple-400 mb-2 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  VIP Metinis + asmeninis ryšys (€65/metus)
                </h3>
                <div className="text-sm text-foreground/70 space-y-2">
                  <p className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-purple-400" />
                    <strong className="text-purple-400">30 dienų grąžinimo laikotarpis</strong>
                  </p>
                  <p>
                    Šiam planui taikomas išplėstas 30 dienų grąžinimo laikotarpis, nes suprantame, 
                    kad asmeninis ryšys reikalauja laiko įvertinti.
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Premium Plan Refund Details */}
          <Card className="bg-gradient-to-br from-purple-950/50 via-violet-950/50 to-slate-950/50 border-purple-500/30 p-6">
            <h2 className="text-xl font-semibold text-purple-400 mb-4 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              &bdquo;VIP Metinis + asmeninis ryšys&ldquo; grąžinimo skaičiavimas
            </h2>
            
            <div className="text-foreground/80 space-y-4">
              <p>
                Jei vartotojas nutraukia &bdquo;VIP Metinis + asmeninis ryšys (6 konsultacijos)&ldquo; planą 
                per 30 dienų nuo įsigijimo, grąžinama suma skaičiuojama taip:
              </p>

              <div className="bg-purple-500/20 rounded-lg p-4 border border-purple-500/40">
                <h3 className="font-semibold text-purple-400 mb-3">Grąžinimo formulė:</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>Konsultacijų dalis (€40 už 6 konsultacijas):</strong></p>
                  <p className="pl-4 text-purple-300 font-mono">
                    €40 − (atliktų konsultacijų skaičius × €6.67) = grąžinama suma
                  </p>
                  <p className="mt-3"><strong>VIP dalis (€25):</strong></p>
                  <p className="pl-4 text-muted-foreground">
                    Grąžinama pagal bendrą platformos prenumeratos politiką (individualus svarstymas)
                  </p>
                </div>
              </div>

              {/* Interactive Calculator */}
              <div className="bg-secondary/20 rounded-lg p-4">
                <button
                  onClick={() => setShowCalculator(!showCalculator)}
                  className="text-purple-400 hover:text-purple-300 text-sm font-medium flex items-center gap-2"
                >
                  <Calculator className="w-4 h-4" />
                  {showCalculator ? 'Slėpti skaičiuoklę' : 'Atidaryti grąžinimo skaičiuoklę'}
                </button>

                {showCalculator && (
                  <div className="mt-4 space-y-3">
                    <div>
                      <label className="text-sm text-foreground/70 block mb-1">
                        Kiek konsultacijų jau panaudota?
                      </label>
                      <select
                        value={usedConsultations}
                        onChange={(e) => setUsedConsultations(Number(e.target.value))}
                        className="bg-secondary/30 border border-purple-500/30 rounded px-3 py-2 text-sm w-full max-w-xs"
                      >
                        {[...Array(7)].map((_, i) => (
                          <option key={i} value={i}>{i} konsultacija(-os)</option>
                        ))}
                      </select>
                    </div>
                    <div className="bg-purple-500/20 rounded-lg p-3">
                      <p className="text-sm text-foreground/70">Konsultacijų dalies grąžinimas:</p>
                      <p className="text-2xl font-bold text-purple-400">€{calculateRefund()}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        (iš €40 konsultacijų dalies)
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Example */}
              <div className="bg-secondary/10 rounded-lg p-4 border border-border/50">
                <h4 className="font-medium text-foreground mb-2">Pavyzdys:</h4>
                <p className="text-sm text-foreground/70">
                  Jei per 30 dienų panaudojote 1 konsultaciją ir nusprendėte nutraukti planą:
                </p>
                <ul className="text-sm text-foreground/70 mt-2 space-y-1 pl-4">
                  <li>• Konsultacijų dalis: €40 − (1 × €6.67) = <strong className="text-purple-400">€33.33</strong></li>
                  <li>• VIP dalis (€25): svarstoma individualiai</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* How to Request Refund */}
          <Card className="bg-gradient-to-br from-violet-950/50 via-indigo-950/50 to-slate-950/50 border-amber-500/30 p-6">
            <h2 className="text-xl font-semibold text-amber-400 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Kaip pateikti grąžinimo prašymą?
            </h2>
            
            <div className="text-foreground/80 space-y-3">
              <p>
                Jei norite nutraukti prenumeratą ir gauti grąžinimą, susisiekite su mumis:
              </p>
              <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/30">
                <p className="font-semibold text-amber-400">
                  <Mail className="w-4 h-4 inline mr-2" />
                  info@numenaxis.com
                </p>
              </div>
              <p className="text-sm">
                Prašyme nurodykite:
              </p>
              <ul className="text-sm space-y-1 pl-4">
                <li>• Jūsų el. pašto adresą (kurį naudojote registruojantis)</li>
                <li>• Prenumeratos planą</li>
                <li>• Grąžinimo priežastį (neprivaloma, bet padeda mums tobulėti)</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4">
                Atsakome per 2-3 darbo dienas. Grąžinimai apdorojami per 5-7 darbo dienas.
              </p>
            </div>
          </Card>

          {/* Important Notes */}
          <Card className="bg-gradient-to-br from-violet-950/50 via-indigo-950/50 to-slate-950/50 border-border/50 p-6">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Svarbi informacija
            </h2>
            
            <ul className="text-sm text-foreground/70 space-y-2">
              <li>• Grąžinimai skaičiuojami nuo prenumeratos pradžios datos</li>
              <li>• 30 dienų grąžinimo laikotarpis taikomas tik &bdquo;VIP Metinis + asmeninis ryšys&ldquo; planui</li>
              <li>• Jau panaudotos konsultacijos atskaitomos iš grąžinamos sumos</li>
              <li>• NUMENAXIS pasilieka teisę atmesti grąžinimo prašymus piktnaudžiavimo atvejais</li>
              <li>• Ši politika gali būti atnaujinta. Apie pakeitimus informuosime el. paštu</li>
            </ul>
          </Card>

          {/* Last updated */}
          <p className="text-center text-xs text-muted-foreground">
            Paskutinį kartą atnaujinta: 2026 m. sausio mėn.
          </p>
        </div>

        {/* Footer */}
        <footer className="mt-12 pt-6 border-t border-border/50 text-center">
          <p className="text-muted-foreground text-sm">
            © 2026 NUMENAXIS. Visos teisės saugomos.
          </p>
          <p className="text-muted-foreground text-xs mt-1">
            Kontaktai: <a href="mailto:info@numenaxis.com" className="text-amber-400 hover:underline">info@numenaxis.com</a>
          </p>
        </footer>
      </div>
    </div>
  );
}
